import { Col, Row } from "react-bootstrap"
import React from "react"
import { CompetitionPageHeader } from "@components/Competition/CompetitionPageHeader/CompetitionPageHeader"
import { CompetitionSeasonGroupSelector } from "@components/Competition/CompetitionSeasonGroupSelector/CompetitionSeasonGroupSelector"
import { getNavigationProps } from "@components/Navigation/getNavigationProps"
import InferNextPropsType from "infer-next-props-type"
import { ParsedUrlQuery } from "querystring"
import { GetStaticPropsContext } from "next"
import * as NextConstants from "constants/next"
import { CompetitionSeasonSelector } from "@components/Competition/CompetitionSeasonSelector/CompetitionSeasonSelector"
import { GroupStandingOld } from "@components/Competition/GroupStandingOld"
import { GroupAPI } from "../../../../../../../../../src/apis/GroupAPI"
import { CompetitionSeasonAPI } from "../../../../../../../../../src/apis/CompetitionSeasonAPI"
import { SeasonAPI } from "../../../../../../../../../src/apis/SeasonAPI"
import { CompetitionAPI } from "../../../../../../../../../src/apis/CompetitionAPI"
import { RSS, SEO } from "../../../../../../../../../src/types/next.js"
import { getCompetitionCanonicalData } from "../../../../../../../../../src/utils/competition"
import { CompetitionConstants } from "../../../../../../../../../src/constants/competitions"
import { CompetitionSeasonGroupTurnSelector } from "@components/Competition/CompetitionSeasonGroupTurnSelector/CompetitionSeasonGroupTurnSelector"
import { NewTitleBanner } from "@components/Banners/NewTitleBanner"
import { BookmakerBonusAPI } from "apis/BookmakerBonusAPI"
import { simplifyBookmakerBonus } from "../../../../../../../../../src/utils/bookmakerBonus"

export default function CompetitionPage({
  competitionCalciocomSlug,
  seasonCalciocomSlug,
  seasons,
  groupCalciocomSlug,
  groups,
  turns,
  turnCalciocomSlug,
  teamStandings,
  competitionDetail,
  headerTitle,
}: InferNextPropsType<typeof getStaticProps>) {
  return (
    <>
      <Row className={"marginZero"}>
        <Col md={12} className={"paddingZero"}>
          <div id="hs-content" className={"hs-border-div paddingZero"}>
            <CompetitionPageHeader
              selected="classifica"
              competitionCalciocomSlug={competitionCalciocomSlug}
              competition={competitionDetail}
            />
            <div className="new-classifica-tab">
              <div className="new-title-banner-container">
                <NewTitleBanner title={headerTitle === undefined ? "Classifica" : headerTitle} col={{ lg: 12 }} />
              </div>
              <Row style={{ marginBottom: "10px" }}>
                <Col xs={12} sm={12} md={4} lg={4} xl={4}>
                  <CompetitionSeasonSelector
                    competitionCalciocomSlug={competitionCalciocomSlug}
                    seasonCalciocomSlug={seasonCalciocomSlug}
                    seasons={seasons}
                    page={"classifica"}
                  />
                </Col>
                {groups.length > 1 && (
                  <Col xs={12} sm={12} md={4} lg={4} xl={4}>
                    <CompetitionSeasonGroupSelector
                      competitionCalciocomSlug={competitionCalciocomSlug}
                      seasonCalciocomSlug={seasonCalciocomSlug}
                      groupCalciocomSlug={groupCalciocomSlug}
                      groups={groups}
                      page={"classifica"}
                    />
                  </Col>
                )}
                <Col xs={12} sm={12} md={4} lg={4} xl={4}>
                  <CompetitionSeasonGroupTurnSelector
                    competitionCalciocomSlug={competitionCalciocomSlug}
                    seasonCalciocomSlug={seasonCalciocomSlug}
                    groupCalciocomSlug={groupCalciocomSlug}
                    turnCalciocomSlug={turnCalciocomSlug}
                    turns={turns}
                    page={"classifica"}
                  />
                </Col>
              </Row>
              <GroupStandingOld
                competitionCalciocomSlug={competitionCalciocomSlug}
                seasonCalciocomSlug={seasonCalciocomSlug}
                teamStandings={teamStandings}
              />
            </div>
          </div>
        </Col>
      </Row>
    </>
  )
}

export async function getStaticPaths() {
  return { paths: [], fallback: "blocking" }
}

export interface IParams extends ParsedUrlQuery {
  competitionCalciocomSlug: string
  seasonCalciocomSlug: string
  groupCalciocomSlug: string
  turnCalciocomSlug: string
}

export async function getStaticProps(context: GetStaticPropsContext<IParams>, index = false) {
  const { competitionCalciocomSlug, seasonCalciocomSlug, groupCalciocomSlug, turnCalciocomSlug } = context.params!

  try {
    const [competitionIds, seasonIds] = await Promise.all([
      CompetitionAPI.getCompetitionIds(competitionCalciocomSlug),
      SeasonAPI.getSeasonIds(seasonCalciocomSlug),
    ])
    const competition = await CompetitionAPI.getCompetition(competitionIds.id)

    const seasons = await SeasonAPI.getSeasonsByCompetitionId(competitionIds.id)
    const season = seasons.find((season) => season.id === seasonIds.id)!

    const competitionSeasonId = await CompetitionSeasonAPI.getIdByCompetitionIdAndSeasonId(competition.id, season.id)

    const groups = await SeasonAPI.getPhaseGroups(competitionSeasonId)
    const group = await GroupAPI.getGroup(groups.find((group) => group.calciocomSlug === groupCalciocomSlug)!.id)

    const turns = group.turns ? group.turns : []
    const turn = (group.turns && group.turns.filter((t) => t.calciocomSlug == turnCalciocomSlug)[0]) || null

    const teamStandings = await GroupAPI.getTeamStandings(group.id, turn?.id)

    const competitionDetail = await CompetitionAPI.getLastSeasonCompetitionDetails(competitionIds.id)

    const title = `Classifica ${competition.name} di calcio  ${season.name}`

    const seo: SEO = {
      title: title,
      description: `La classifica del Campionato di ${competition.name} ${season.name}, con tutti i dati su partite vinte, perse e pareggiate, gol fatti, gol subiti e differenza reti.`,
      keywords: [
        `${competition.name}`,
        `${season?.name}`,
        `${group?.name}`,
        `${turn?.name}`,
        "calcio",
        "Classifica",
        "Risultati",
      ],
      ...(await getCompetitionCanonicalData(
        competition,
        season,
        group,
        turn,
        `/campionato/${competitionCalciocomSlug}/stagione/${seasonCalciocomSlug}/gruppo/${groupCalciocomSlug}/giornata/${turnCalciocomSlug}/classifica`,
      )),
      canonicals: [`/campionato/${competitionCalciocomSlug}/classifica`],
      index: index,
    }

    let rss: RSS | null = null
    if (CompetitionConstants.TOP_COMPETITIONS_IDS.includes(competition.id)) {
      rss = {
        competitionCalcioComSlug: competitionCalciocomSlug,
      }
    }
    const titlePhase = turnCalciocomSlug.split("-")[0]
    const containsNumber = parseInt(titlePhase)
    const formattedTitle = containsNumber
      ? `Classifica ${competition.name} ${season.name} ${titlePhase}° giornata`
      : `Classifica ${competition.name} ${season.name} ${titlePhase}`

    return {
      props: {
        competitionCalciocomSlug: competitionCalciocomSlug,
        seasonCalciocomSlug: seasonCalciocomSlug,
        groupCalciocomSlug: groupCalciocomSlug,
        turnCalciocomSlug: turnCalciocomSlug,
        seasons,
        groups,
        teamStandings,
        competitionDetail,
        turns,
        headerTitle: formattedTitle,
        navigationProps: await getNavigationProps(competitionIds.id),
        title,
        seo,
        rss,
      },
      revalidate: NextConstants.REVALIDATE,
    }
  } catch (error) {
    console.error(error)
    return {
      notFound: true,
    }
  }
}
